import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Database, 
  RefreshCw, 
  Loader2,
  AlertCircle,
  Plug,
  Upload,
  Zap
} from 'lucide-react';
import ConnectionWizard from './ConnectionWizard';
import ConnectionCard from './ConnectionCard';
import ImportWizard from './ImportWizard';
import ImportCard from './ImportCard';
import TestDataHub from './TestDataHub';

export default function ExternalDB() {
  const [activeTab, setActiveTab] = useState('connections');
  const [connections, setConnections] = useState([]);
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [testDataSource, setTestDataSource] = useState(null);
  const [testImportSource, setTestImportSource] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadConnections();
    loadImports();
  }, []);

  const seedTestConnections = async () => {
    try {
      console.log('🌱 Seeding test connections (and creating system user)...');
      const response = await fetch('/api/seed-test-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        const text = await response.text();
        console.error('API returned error:', response.status, text);
        alert(`Failed to seed test connections: ${response.status} ${response.statusText}`);
        return;
      }
      
      const result = await response.json();
      console.log('✅ Seed result:', result);
      alert(`Successfully seeded ${result.count} test connections!`);
      
      // Reload connections
      await loadConnections();
    } catch (error) {
      console.error('❌ Could not seed test connections:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const loadImports = async () => {
    try {
      const userId = localStorage.getItem('userId') || 'temp-user-id';
      const response = await fetch('/api/data-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'list-imports',
          data: { userId }
        })
      });
      const result = await response.json();
      if (result.success) {
        setImports(result.imports);
      }
    } catch (error) {
      console.error('Failed to load imports:', error);
    }
  };

  const loadConnections = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get current user ID from localStorage or auth context
      // Use a valid UUID for development (00000000-0000-0000-0000-000000000000)
      const userId = localStorage.getItem('userId') || '00000000-0000-0000-0000-000000000000';

      const response = await fetch('/api/external-db', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'list-connections',
          data: { userId }
        })
      });

      const result = await response.json();
      
      if (result.success) {
        setConnections(result.connections || []);
      } else {
        // Only show error if it's not a "no connections" scenario
        if (result.connections && result.connections.length === 0) {
          setConnections([]);
        } else if (!result.error?.includes('uuid') && !result.error?.includes('temp-user')) {
          // Don't show errors related to temp user IDs
          setError(result.error);
        } else {
          // Gracefully handle temp user scenario
          setConnections([]);
        }
      }
    } catch (err) {
      console.error('Error loading connections:', err);
      // Only show critical errors, not temp user issues
      if (!err.message?.includes('uuid') && !err.message?.includes('temp-user')) {
        setError(err.message);
      } else {
        setConnections([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConnection = (newConnection) => {
    setConnections([newConnection, ...connections]);
  };

  const handleImportComplete = (importResult) => {
    loadImports();
  };

  const handleArchiveImport = async (datasetId) => {
    try {
      const userId = localStorage.getItem('userId') || 'temp-user-id';
      const response = await fetch('/api/data-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'archive-import',
          data: { datasetId, userId }
        })
      });
      const result = await response.json();
      if (result.success) {
        setImports(imports.filter(i => i.id !== datasetId));
      }
    } catch (error) {
      console.error('Error archiving import:', error);
    }
  };

  const handleDeleteImport = async (datasetId) => {
    try {
      const userId = localStorage.getItem('userId') || 'temp-user-id';
      const response = await fetch('/api/data-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'delete-import',
          data: { datasetId, userId }
        })
      });
      const result = await response.json();
      if (result.success) {
        setImports(imports.filter(i => i.id !== datasetId));
      }
    } catch (error) {
      console.error('Error deleting import:', error);
    }
  };

  const handleViewImport = (dataset) => {
    alert('View functionality coming soon!');
  };

  const handleDeleteConnection = async (connectionId) => {
    if (!window.confirm('Are you sure you want to delete this connection?')) {
      return;
    }

    try {
      const userId = localStorage.getItem('userId') || '00000000-0000-0000-0000-000000000000';

      const response = await fetch('/api/external-db', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'delete-connection',
          data: { connectionId, userId }
        })
      });

      const result = await response.json();
      
      if (result.success) {
        setConnections(connections.filter(c => c.id !== connectionId));
      } else {
        alert('Failed to delete connection: ' + result.error);
      }
    } catch (err) {
      console.error('Error deleting connection:', err);
      alert('Failed to delete connection: ' + err.message);
    }
  };

  const handleNormalizeConnection = async (connectionId) => {
    try {
      const userId = localStorage.getItem('userId') || '00000000-0000-0000-0000-000000000000';
      
      const response = await fetch('/api/external-db', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'normalize-external-data',
          data: { connectionId, userId }
        })
      });

      const result = await response.json();
      
      if (result.success) {
        alert(`✅ Normalization complete!\n\n` +
              `Records processed: ${result.totalRecords}\n` +
              `Percentiles calculated: ${result.percentileMetrics} metrics\n` +
              `Schema mapped: ${result.mappedColumns} columns\n\n` +
              `${result.errors?.length ? `⚠️ Errors: ${result.errors.join(', ')}` : ''}`);
        
        // Reload connections to show updated metadata
        await loadConnections();
      } else {
        alert('❌ Normalization failed: ' + result.error);
      }
    } catch (error) {
      console.error('Error normalizing connection:', error);
      alert('❌ Error: ' + error.message);
    }
  };

  const handleTestConnection = async (connectionId) => {
    // Update connection status to testing
    setConnections(connections.map(c => 
      c.id === connectionId ? { ...c, status: 'testing' } : c
    ));

    try {
      const connection = connections.find(c => c.id === connectionId);
      
      const response = await fetch('/api/external-db', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'test-connection',
          data: {
            dbType: connection.db_type,
            connectionMethod: connection.connection_method,
            host: connection.host,
            port: connection.port,
            database: connection.database_name,
            username: connection.username,
            password: '', // Need to re-prompt or store decryptable
            sshHost: connection.ssh_host,
            sshPort: connection.ssh_port,
            sshUsername: connection.ssh_username,
            sslEnabled: connection.ssl_enabled
          }
        })
      });

      const result = await response.json();
      
      // Update connection with test result
      setConnections(connections.map(c => 
        c.id === connectionId ? {
          ...c,
          status: result.success ? 'active' : 'error',
          test_latency_ms: result.latency,
          last_tested_at: new Date().toISOString(),
          last_error: result.error || null
        } : c
      ));
    } catch (err) {
      console.error('Error testing connection:', err);
      setConnections(connections.map(c => 
        c.id === connectionId ? {
          ...c,
          status: 'error',
          last_error: err.message
        } : c
      ));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading connections...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Tabs */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-500/20 rounded-lg p-2">
              <Plug className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">External Data</h2>
              <p className="text-sm text-slate-400">
                Connect to databases or import data files
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => activeTab === 'connections' ? loadConnections() : loadImports()}
            className="flex items-center space-x-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          {activeTab === 'connections' ? (
            <button
              onClick={() => setShowWizard(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Connection</span>
            </button>
          ) : (
            <button
              onClick={() => setShowImportWizard(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              <Upload className="w-4 h-4" />
              <span>Import Dataset</span>
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center space-x-2 border-b border-slate-700">
        <button
          onClick={() => setActiveTab('connections')}
          className={`px-4 py-2 font-medium transition-all ${
            activeTab === 'connections'
              ? 'text-blue-400 border-b-2 border-blue-400'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <div className="flex items-center space-x-2">
            <Plug className="w-4 h-4" />
            <span>Live Connections</span>
            {connections.length > 0 && (
              <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full text-xs">
                {connections.length}
              </span>
            )}
          </div>
        </button>
        <button
          onClick={() => setActiveTab('imports')}
          className={`px-4 py-2 font-medium transition-all ${
            activeTab === 'imports'
              ? 'text-blue-400 border-b-2 border-blue-400'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <div className="flex items-center space-x-2">
            <Upload className="w-4 h-4" />
            <span>Data Imports</span>
            {imports.length > 0 && (
              <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full text-xs">
                {imports.length}
              </span>
            )}
          </div>
        </button>
        <button
          onClick={() => setActiveTab('test-data')}
          className={`px-4 py-2 font-medium transition-all ${
            activeTab === 'test-data'
              ? 'text-yellow-400 border-b-2 border-yellow-400'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <div className="flex items-center space-x-2">
            <Zap className="w-4 h-4" />
            <span>Test Data</span>
            <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded-full text-xs">
              NEW
            </span>
          </div>
        </button>
      </div>

      {/* Original Header (hidden, replaced by tabs above) */}
      <div className="hidden items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="bg-blue-500/20 rounded-lg p-2">
            <Plug className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">External Database Connector</h2>
            <p className="text-sm text-slate-400">
              Connect to on-premises or cloud SQL databases with SSH tunnel support
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={loadConnections}
            className="flex items-center space-x-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => setShowWizard(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Connection</span>
          </button>
        </div>
      </div>

      {/* Status Banner */}
      {!error && connections.length === 0 && activeTab === 'connections' && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <Database className="w-5 h-5 text-green-400 mt-0.5" />
            <div>
              <p className="text-green-400 font-medium">Internal Database Connected</p>
              <p className="text-sm text-green-300 mt-1">
                Your SRI platform database is active. Add external database connections to query additional data sources.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message - Only for real errors */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
            <div>
              <p className="text-red-400 font-medium">Connection Error</p>
              <p className="text-sm text-red-300 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Bar */}
      {connections.length > 0 && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-4">
            <div className="text-sm text-slate-400 mb-1">Total Connections</div>
            <div className="text-2xl font-bold text-white">{connections.length}</div>
          </div>
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-4">
            <div className="text-sm text-slate-400 mb-1">Active</div>
            <div className="text-2xl font-bold text-green-400">
              {connections.filter(c => c.status === 'active').length}
            </div>
          </div>
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-4">
            <div className="text-sm text-slate-400 mb-1">Errors</div>
            <div className="text-2xl font-bold text-red-400">
              {connections.filter(c => c.status === 'error').length}
            </div>
          </div>
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-4">
            <div className="text-sm text-slate-400 mb-1">Avg Latency</div>
            <div className="text-2xl font-bold text-blue-400">
              {connections.filter(c => c.test_latency_ms).length > 0
                ? Math.round(
                    connections
                      .filter(c => c.test_latency_ms)
                      .reduce((sum, c) => sum + c.test_latency_ms, 0) /
                    connections.filter(c => c.test_latency_ms).length
                  )
                : 0}ms
            </div>
          </div>
        </div>
      )}

      {/* Live Connections Tab */}
      {activeTab === 'connections' && connections.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {connections.map((connection) => (
            <ConnectionCard
              key={connection.id}
              connection={connection}
              onDelete={handleDeleteConnection}
              onTest={handleTestConnection}
              onNormalize={handleNormalizeConnection}
            />
          ))}
        </div>
      )}

      {activeTab === 'connections' && connections.length === 0 && (
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-12 text-center">
          <div className="bg-slate-700/50 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
            <Database className="w-10 h-10 text-slate-400" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">No Connections Yet</h3>
          <p className="text-slate-400 mb-6 max-w-md mx-auto">
            Connect to your external SQL databases to query and analyze data alongside your SRI platform data.
          </p>
          <div className="flex items-center justify-center space-x-4">
            <button
              onClick={() => setShowWizard(true)}
              className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              <Plus className="w-5 h-5" />
              <span>Add Your First Connection</span>
            </button>
            <button
              onClick={seedTestConnections}
              className="inline-flex items-center space-x-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
            >
              <Zap className="w-5 h-5" />
              <span>Seed Test Databases</span>
            </button>
          </div>

          {/* Features List */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
            <div className="bg-slate-700/30 rounded-lg p-4">
              <div className="text-2xl mb-2">🔐</div>
              <h4 className="font-semibold text-white mb-1">SSH Tunnel Support</h4>
              <p className="text-sm text-slate-400">
                Securely connect to on-premises databases behind firewalls
              </p>
            </div>
            <div className="bg-slate-700/30 rounded-lg p-4">
              <div className="text-2xl mb-2">🔍</div>
              <h4 className="font-semibold text-white mb-1">Auto Schema Detection</h4>
              <p className="text-sm text-slate-400">
                Automatically discover tables, columns, and data types
              </p>
            </div>
            <div className="bg-slate-700/30 rounded-lg p-4">
              <div className="text-2xl mb-2">🤖</div>
              <h4 className="font-semibold text-white mb-1">AI-Powered Mapping</h4>
              <p className="text-sm text-slate-400">
                Smart column mapping to SRI schema with confidence scores
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Data Imports Tab */}
      {activeTab === 'imports' && imports.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {imports.map((dataset) => (
            <ImportCard
              key={dataset.id}
              dataset={dataset}
              onArchive={handleArchiveImport}
              onDelete={handleDeleteImport}
              onView={handleViewImport}
            />
          ))}
        </div>
      )}

      {activeTab === 'imports' && imports.length === 0 && (
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-12 text-center">
          <div className="bg-slate-700/50 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
            <Upload className="w-10 h-10 text-slate-400" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">No Imported Datasets</h3>
          <p className="text-slate-400 mb-6 max-w-md mx-auto">
            Import CSV, Excel, or JSON files to create separate datasets within your database.
          </p>
          <button
            onClick={() => setShowImportWizard(true)}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            <Upload className="w-5 h-5" />
            <span>Import Your First Dataset</span>
          </button>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
            <div className="bg-slate-700/30 rounded-lg p-4">
              <div className="text-2xl mb-2">📄</div>
              <h4 className="font-semibold text-white mb-1">CSV & Excel</h4>
              <p className="text-sm text-slate-400">
                Upload spreadsheets with student data matching SRI schema
              </p>
            </div>
            <div className="bg-slate-700/30 rounded-lg p-4">
              <div className="text-2xl mb-2">✓</div>
              <h4 className="font-semibold text-white mb-1">Auto Validation</h4>
              <p className="text-sm text-slate-400">
                Automatic schema validation and column mapping
              </p>
            </div>
            <div className="bg-slate-700/30 rounded-lg p-4">
              <div className="text-2xl mb-2">📊</div>
              <h4 className="font-semibold text-white mb-1">Separate Tables</h4>
              <p className="text-sm text-slate-400">
                Each import gets its own table for data isolation
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Test Data Hub Tab */}
      {activeTab === 'test-data' && (
        <TestDataHub 
          onQuickConnect={(source) => {
            // Pre-fill connection wizard with test data
            setTestDataSource(source);
            setShowWizard(true);
          }}
          onQuickImport={(source) => {
            // Pre-fill import wizard with test data
            setTestImportSource(source);
            setShowImportWizard(true);
          }}
        />
      )}

      {/* Connection Wizard Modal */}
      {showWizard && (
        <ConnectionWizard
          onClose={() => {
            setShowWizard(false);
            setTestDataSource(null);
          }}
          onSave={handleSaveConnection}
          initialData={testDataSource}
        />
      )}

      {/* Import Wizard Modal */}
      {showImportWizard && (
        <ImportWizard
          onClose={() => {
            setShowImportWizard(false);
            setTestImportSource(null);
          }}
          onComplete={handleImportComplete}
          initialData={testImportSource}
        />
      )}
    </div>
  );
}
